// @ts-nocheck
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDevice } from '../../Api/newSockets.api';
import { isDeviceSocketLive } from '../../utils/deviceSocket';
import { DeviceTypeId } from '../../constants/deviceTypes';

interface NetworkHost {
  ip: string;
  mac: string;
  hostname: string;
  device_type: 'JETSON' | 'SIEMENS_PLC_HMI' | 'OTHER';
  jetson_score: number;
  siemens_score: number;
  open_ports: {
    jetson_typical: number[];
    siemens_typical: number[];
  };
  classification_reasons: string[];
}

interface NetworkResult {
  network: string;
  iface: string;
  local_ip: string;
  hosts: NetworkHost[];
}

interface NetworkGraphProps {
  devices: any[];
  hostDevices: any[];
}

interface GraphNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  type: 'host' | 'jetson' | 'plc' | 'camera' | 'other' | 'hive';
  label: string;
  sublabel?: string;
  ip?: string;
  mac?: string;
  online?: boolean;
  deviceId?: number;
  networkHost?: NetworkHost;
}

interface GraphLink {
  source: string;
  target: string;
  type: 'network' | 'device';
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({ devices, hostDevices }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<GraphNode[]>([]);
  const linksRef = useRef<GraphLink[]>([]);
  const graphBuiltRef = useRef(false);

  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState<NetworkResult[]>([]);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [lastScanTime, setLastScanTime] = useState<Date | null>(null);

  const { scanNetwork, updateDeviceSettings } = useDevice();

  // Get online host devices that can run the scanner
  const onlineHosts = useMemo(() => {
    return hostDevices.filter((h: any) => isDeviceSocketLive(h));
  }, [hostDevices]);

  // Load persisted scan results from host device settings on mount
  useEffect(() => {
    if (hostDevices.length > 0 && scanResults.length === 0) {
      // Look for persisted scan data in any host device's settings
      for (const host of hostDevices) {
        const persistedScan = host.settings?.lastNetworkScan;
        if (persistedScan && Array.isArray(persistedScan.results)) {
          setScanResults(persistedScan.results);
          if (persistedScan.timestamp) {
            setLastScanTime(new Date(persistedScan.timestamp));
          }
          break; // Use the first found scan data
        }
      }
    }
  }, [hostDevices]);

  // Build graph from devices and scan results
  const buildGraph = useCallback(() => {
    if (!dimensions) return; // Wait for dimensions to be measured

    // Preserve existing node positions to avoid visual glitches
    const existingPositions = new Map<string, { x: number; y: number; vx: number; vy: number }>();
    nodesRef.current.forEach(node => {
      existingPositions.set(node.id, { x: node.x, y: node.y, vx: node.vx, vy: node.vy });
    });

    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;

    // Helper to get position (existing or new)
    const getPosition = (id: string, defaultX: number, defaultY: number, randomOffset = 0) => {
      const existing = existingPositions.get(id);
      if (existing) {
        return existing;
      }
      return {
        x: defaultX + (randomOffset > 0 ? (Math.random() - 0.5) * randomOffset : 0),
        y: defaultY + (randomOffset > 0 ? (Math.random() - 0.5) * randomOffset : 0),
        vx: 0,
        vy: 0,
      };
    };

    // Add Hive server as central node
    const hivePos = getPosition('hive', centerX, centerY);
    nodes.push({
      id: 'hive',
      ...hivePos,
      type: 'hive',
      label: 'Hive Server',
      sublabel: '10.200.0.23',
      ip: '10.200.0.23',
    });

    // Add known devices from the system
    const angleStep = (2 * Math.PI) / Math.max(hostDevices.length, 1);
    hostDevices.forEach((host: any, index: number) => {
      const angle = angleStep * index - Math.PI / 2;
      const radius = 180;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;

      const isOnline = isDeviceSocketLive(host);
      const hostIp = host.hardwareInfo?.ip || host.settings?.ip || host.settings?.public_ip;
      const nodeId = `device-${host.id}`;
      const pos = getPosition(nodeId, x, y, 20);
      nodes.push({
        id: nodeId,
        ...pos,
        type: 'host',
        label: host.name || `Host ${host.id}`,
        sublabel: 'Jetson Host',
        ip: hostIp,
        online: isOnline,
        deviceId: host.id,
      });

      links.push({
        source: 'hive',
        target: `device-${host.id}`,
        type: 'device',
      });

      // Add child devices
      const childDevices = devices.filter((d: any) => d.connectedTo === host.id);
      const childAngleStep = Math.PI / Math.max(childDevices.length + 1, 2);
      childDevices.forEach((child: any, childIndex: number) => {
        const childAngle = angle - Math.PI / 4 + childAngleStep * (childIndex + 1);
        const childRadius = 80;
        const childX = x + Math.cos(childAngle) * childRadius;
        const childY = y + Math.sin(childAngle) * childRadius;

        let childType: GraphNode['type'] = 'other';
        if (child.deviceTypes?.id === DeviceTypeId.CAMERA) childType = 'camera';
        else if (child.deviceTypes?.id === DeviceTypeId.PLC) childType = 'plc';

        const childIp = child.hardwareInfo?.ip || child.settings?.ip || child.settings?.public_ip;
        const childNodeId = `device-${child.id}`;
        const childPos = getPosition(childNodeId, childX, childY, 10);
        nodes.push({
          id: childNodeId,
          ...childPos,
          type: childType,
          label: child.name || `Device ${child.id}`,
          sublabel: child.deviceTypes?.name || '',
          ip: childIp,
          online: isDeviceSocketLive(child),
          deviceId: child.id,
        });

        links.push({
          source: `device-${host.id}`,
          target: childNodeId,
          type: 'device',
        });
      });
    });

    // Build a map of all known device IPs for matching
    // A device may have multiple IPs (VPN, WiFi, etc.)
    const knownDeviceIps = new Map<string, { device: any; nodeId: string }>();
    const addDeviceIp = (device: any, nodeId: string) => {
      // Check various places where IP might be stored
      const ips = [
        device.hardwareInfo?.ip,
        device.settings?.ip,
        device.settings?.public_ip,
        device.ip,
      ].filter(Boolean);
      ips.forEach(ip => knownDeviceIps.set(ip, { device, nodeId }));
    };

    devices.forEach((d: any) => addDeviceIp(d, `device-${d.id}`));
    hostDevices.forEach((h: any) => addDeviceIp(h, `device-${h.id}`));

    // Also track local IPs from scan results (these are the scanning device's IPs)
    // Map local_ip to the host device that scanned from that IP
    const localIpToHost = new Map<string, any>();
    scanResults.forEach(network => {
      // Find which host device has this local_ip
      const host = hostDevices.find((h: any) => {
        const hostIps = [
          h.hardwareInfo?.ip,
          h.settings?.ip,
          h.settings?.public_ip,
        ].filter(Boolean);
        return hostIps.some(ip => ip === network.local_ip);
      });
      if (host) {
        localIpToHost.set(network.local_ip, host);
        // Also add all the local IPs to known device IPs
        knownDeviceIps.set(network.local_ip, { device: host, nodeId: `device-${host.id}` });
      }
    });

    // Add discovered network hosts from scan
    scanResults.forEach((network, netIndex) => {
      const networkAngle = (2 * Math.PI / Math.max(scanResults.length, 1)) * netIndex + Math.PI / 4;
      const networkRadius = 420;

      network.hosts.forEach((host, hostIndex) => {
        // Check if this IP belongs to a known device
        const knownDevice = knownDeviceIps.get(host.ip);
        if (knownDevice) {
          // Update the existing node with scan data
          const existingNode = nodes.find(n => n.id === knownDevice.nodeId);
          if (existingNode) {
            existingNode.networkHost = host;
            existingNode.mac = host.mac;
            // Add scanned IP to sublabel if different from primary
            if (existingNode.ip && existingNode.ip !== host.ip) {
              existingNode.sublabel = `${existingNode.sublabel || ''} (${host.ip})`.trim();
            }
          }
          return;
        }

        const hostAngle = networkAngle + (hostIndex - network.hosts.length / 2) * 0.15;
        const defaultX = centerX + Math.cos(hostAngle) * networkRadius;
        const defaultY = centerY + Math.sin(hostAngle) * networkRadius;

        let nodeType: GraphNode['type'] = 'other';
        if (host.device_type === 'JETSON') nodeType = 'jetson';
        else if (host.device_type === 'SIEMENS_PLC_HMI') nodeType = 'plc';

        // If hostname is unknown, use IP as label and don't show IP again below
        const hasHostname = host.hostname !== 'unknown';
        const scanNodeId = `scan-${host.ip}`;
        const scanPos = getPosition(scanNodeId, defaultX, defaultY, 30);
        nodes.push({
          id: scanNodeId,
          ...scanPos,
          type: nodeType,
          label: hasHostname ? host.hostname : host.ip,
          sublabel: host.device_type,
          ip: hasHostname ? host.ip : undefined,  // Only set ip if we have a real hostname
          mac: host.mac,
          networkHost: host,
        });

        // Link to nearest known Jetson or Hive
        const nearestHost = hostDevices.find((h: any) => {
          const hostIp = h.hardwareInfo?.ip || h.settings?.ip || h.settings?.public_ip;
          return hostIp && hostIp.split('.').slice(0, 3).join('.') === host.ip.split('.').slice(0, 3).join('.');
        });

        links.push({
          source: nearestHost ? `device-${nearestHost.id}` : 'hive',
          target: `scan-${host.ip}`,
          type: 'network',
        });
      });
    });

    nodesRef.current = nodes;
    linksRef.current = links;
  }, [devices, hostDevices, scanResults, dimensions]);

  // Handle resize - use layoutEffect to measure before paint
  useLayoutEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.clientWidth;
        const newHeight = Math.max(600, window.innerHeight - 200);
        setDimensions(prev => {
          // Only update if dimensions actually changed
          if (prev && prev.width === newWidth && prev.height === newHeight) {
            return prev;
          }
          return { width: newWidth, height: newHeight };
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Build graph when dimensions first become available or when scan results change
  useEffect(() => {
    if (!dimensions) return;
    buildGraph();
    graphBuiltRef.current = true;
  }, [dimensions, scanResults, hostDevices.length, devices.length]);

  // Force simulation - only start when dimensions are ready
  useEffect(() => {
    if (!dimensions) return;

    const simulate = () => {
      const nodes = nodesRef.current;
      const links = linksRef.current;

      if (nodes.length === 0) {
        animationRef.current = requestAnimationFrame(simulate);
        return;
      }

      // Apply forces
      nodes.forEach((node, i) => {
        // Repulsion from other nodes
        nodes.forEach((other, j) => {
          if (i === j) return;
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 2000 / (dist * dist);
          node.vx += (dx / dist) * force;
          node.vy += (dy / dist) * force;
        });

        // Center gravity
        const centerX = dimensions.width / 2;
        const centerY = dimensions.height / 2;
        node.vx += (centerX - node.x) * 0.001;
        node.vy += (centerY - node.y) * 0.001;
      });

      // Link forces
      links.forEach((link) => {
        const source = nodes.find((n) => n.id === link.source);
        const target = nodes.find((n) => n.id === link.target);
        if (!source || !target) return;

        const dx = target.x - source.x;
        const dy = target.y - source.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const idealDist = link.type === 'device' ? 120 : 280;
        const force = (dist - idealDist) * 0.01;

        source.vx += (dx / dist) * force;
        source.vy += (dy / dist) * force;
        target.vx -= (dx / dist) * force;
        target.vy -= (dy / dist) * force;
      });

      // Apply velocity and damping
      nodes.forEach((node) => {
        if (node.id === 'hive') {
          // Keep hive centered
          node.x = dimensions.width / 2;
          node.y = dimensions.height / 2;
          node.vx = 0;
          node.vy = 0;
          return;
        }
        node.vx *= 0.9;
        node.vy *= 0.9;
        node.x += node.vx;
        node.y += node.vy;

        // Boundary constraints
        node.x = Math.max(50, Math.min(dimensions.width - 50, node.x));
        node.y = Math.max(50, Math.min(dimensions.height - 50, node.y));
      });

      // Render
      renderCanvas();
      animationRef.current = requestAnimationFrame(simulate);
    };

    simulate();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [dimensions]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dimensions) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const nodes = nodesRef.current;
    const links = linksRef.current;

    // Clear
    ctx.fillStyle = '#0d1117';
    ctx.fillRect(0, 0, dimensions.width, dimensions.height);

    // Grid
    ctx.strokeStyle = 'rgba(83, 189, 228, 0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < dimensions.width; x += 50) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, dimensions.height);
      ctx.stroke();
    }
    for (let y = 0; y < dimensions.height; y += 50) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(dimensions.width, y);
      ctx.stroke();
    }

    // Draw links
    links.forEach((link) => {
      const source = nodes.find((n) => n.id === link.source);
      const target = nodes.find((n) => n.id === link.target);
      if (!source || !target) return;

      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      // Device links (initial devices) are solid, network links (scanned) are more transparent
      ctx.strokeStyle = link.type === 'device' ? 'rgba(83, 189, 228, 0.7)' : 'rgba(255, 152, 0, 0.4)';
      ctx.lineWidth = link.type === 'device' ? 2.5 : 1;
      ctx.stroke();
    });

    // Draw nodes
    nodes.forEach((node) => {
      const isHovered = hoveredNode?.id === node.id;
      const isSelected = selectedNode?.id === node.id;
      const isOther = node.type === 'other';
      // Check if this is an initial (known) device vs scanned device
      const isInitialDevice = node.id === 'hive' || node.id.startsWith('device-');
      const isScanned = node.id.startsWith('scan-');

      // Larger sizes: hive 45, host 32, jetson/plc/camera 26, other 20
      const radius = node.type === 'hive' ? 45 : node.type === 'host' ? 32 :
                     node.type === 'other' ? 20 : 26;

      // Initial devices are solid, scanned devices are semi-transparent
      const baseAlpha = isScanned ? 0.55 : 1;
      const strokeAlpha = isScanned ? 0.6 : 1;
      const borderWidth = isInitialDevice ? 3 : 1.5;

      // Glow
      if (isHovered || isSelected) {
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, radius * 2);
        gradient.addColorStop(0, getNodeColor(node.type, isScanned ? 0.15 : 0.4));
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius * 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = node.online === false ? (isScanned ? 'rgba(55, 65, 81, 0.3)' : '#374151') : getNodeColor(node.type, baseAlpha);
      ctx.fill();
      ctx.strokeStyle = node.online === false ? (isScanned ? 'rgba(107, 114, 128, 0.4)' : '#6b7280') : getNodeColor(node.type, strokeAlpha);
      ctx.lineWidth = isSelected ? borderWidth + 1 : borderWidth;
      ctx.stroke();

      // Icon
      ctx.globalAlpha = isScanned ? 0.7 : 1;
      ctx.fillStyle = '#fff';
      ctx.font = `${radius * 0.7}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(getNodeIcon(node.type), node.x, node.y);

      // Label (device name)
      ctx.font = '12px monospace';
      ctx.fillStyle = isScanned ? 'rgba(229, 231, 235, 0.65)' : '#e5e7eb';
      ctx.textAlign = 'center';
      ctx.fillText(node.label, node.x, node.y + radius + 16);

      // Track vertical offset for subsequent lines
      let labelOffset = 30;

      // Show IP if available (on line 2)
      if (node.ip) {
        ctx.font = '10px monospace';
        ctx.fillStyle = isScanned ? 'rgba(156, 163, 175, 0.6)' : '#9ca3af';
        ctx.fillText(node.ip, node.x, node.y + radius + labelOffset);
        labelOffset += 12;
      }

      // Show sublabel (type) on next line if different from IP
      if (node.sublabel && node.sublabel !== node.ip) {
        ctx.font = '9px monospace';
        ctx.fillStyle = isScanned ? 'rgba(156, 163, 175, 0.55)' : '#6b7280';
        ctx.fillText(node.sublabel, node.x, node.y + radius + labelOffset);
      }
      ctx.globalAlpha = 1;

      // Online indicator
      if (node.online !== undefined) {
        ctx.beginPath();
        ctx.arc(node.x + radius - 4, node.y - radius + 4, 6, 0, Math.PI * 2);
        ctx.fillStyle = node.online ? '#22c55e' : '#ef4444';
        ctx.fill();
        ctx.strokeStyle = '#0d1117';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    });
  }, [hoveredNode, selectedNode, dimensions]);

  const getNodeColor = (type: GraphNode['type'], alpha: number) => {
    const colors: Record<string, string> = {
      hive: `rgba(83, 189, 228, ${alpha})`,
      host: `rgba(75, 153, 135, ${alpha})`,
      jetson: `rgba(118, 185, 0, ${alpha})`,
      plc: `rgba(255, 152, 0, ${alpha})`,
      camera: `rgba(156, 39, 176, ${alpha})`,
      other: `rgba(107, 114, 128, ${alpha})`,
    };
    return colors[type] || colors.other;
  };

  const getNodeIcon = (type: GraphNode['type']) => {
    const icons: Record<string, string> = {
      hive: '🌐',
      host: '📟',
      jetson: '🤖',
      plc: '⚙️',
      camera: '📷',
      other: '❓',
    };
    return icons[type] || icons.other;
  };

  // Helper to get node radius
  const getNodeRadius = (type: GraphNode['type']) => {
    return type === 'hive' ? 45 : type === 'host' ? 32 : type === 'other' ? 20 : 26;
  };

  // Handle mouse events
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const nodes = nodesRef.current;
    const hovered = nodes.find((node) => {
      const radius = getNodeRadius(node.type);
      const dx = node.x - x;
      const dy = node.y - y;
      return Math.sqrt(dx * dx + dy * dy) < radius;
    });

    setHoveredNode(hovered || null);
    canvas.style.cursor = hovered ? 'pointer' : 'default';
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const nodes = nodesRef.current;
    const clicked = nodes.find((node) => {
      const radius = getNodeRadius(node.type);
      const dx = node.x - x;
      const dy = node.y - y;
      return Math.sqrt(dx * dx + dy * dy) < radius;
    });

    setSelectedNode(clicked || null);
  }, []);

  // Scan network
  const handleScan = async () => {
    if (scanning || onlineHosts.length === 0) return;

    setScanning(true);
    setScanResults([]);

    try {
      // Scan from each online host device
      const results: NetworkResult[] = [];
      for (const host of onlineHosts) {
        try {
          const result = await scanNetwork(host.id);
          if (result && Array.isArray(result)) {
            results.push(...result);
          }
        } catch (err) {
          console.error(`Scan failed for host ${host.id}:`, err);
        }
      }

      // Deduplicate by IP
      const seen = new Set<string>();
      const deduped = results.map((net) => ({
        ...net,
        hosts: net.hosts.filter((h) => {
          if (seen.has(h.ip)) return false;
          seen.add(h.ip);
          return true;
        }),
      }));

      setScanResults(deduped);
      const scanTime = new Date();
      setLastScanTime(scanTime);

      // Persist scan results to first online host device's settings
      if (onlineHosts.length > 0 && deduped.length > 0) {
        const hostToSave = onlineHosts[0];
        try {
          await updateDeviceSettings(hostToSave.id, {
            ...hostToSave.settings,
            lastNetworkScan: {
              results: deduped,
              timestamp: scanTime.toISOString(),
            },
          });
        } catch (err) {
          console.error('Failed to persist scan results:', err);
        }
      }
    } catch (err) {
      console.error('Network scan failed:', err);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '1rem',
        background: 'rgba(13, 17, 23, 0.8)',
        borderRadius: '12px',
        border: '1px solid rgba(83, 189, 228, 0.2)',
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#53bde4', fontSize: '1.2rem', fontWeight: 'bold' }}>
            Network Topology
          </h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.85rem' }}>
            {nodesRef.current.length} nodes • {linksRef.current.length} connections
            {lastScanTime && ` • Last scan: ${lastScanTime.toLocaleTimeString()}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleScan}
            disabled={scanning || onlineHosts.length === 0}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: '1px solid #53bde4',
              background: scanning ? 'rgba(83, 189, 228, 0.1)' : 'rgba(83, 189, 228, 0.2)',
              color: '#53bde4',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              cursor: scanning || onlineHosts.length === 0 ? 'not-allowed' : 'pointer',
              opacity: scanning || onlineHosts.length === 0 ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {scanning ? (
              <>
                <span style={{ animation: 'spin 1s linear infinite' }}>⟳</span>
                Scanning...
              </>
            ) : (
              <>
                <span>🔍</span>
                Scan Network
              </>
            )}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        background: 'rgba(13, 17, 23, 0.6)',
        borderRadius: '8px',
        fontSize: '0.8rem',
      }}>
        {[
          { type: 'hive', label: 'Hive Server' },
          { type: 'host', label: 'Jetson Host' },
          { type: 'plc', label: 'PLC/HMI' },
          { type: 'camera', label: 'Camera' },
          { type: 'jetson', label: 'Discovered Jetson' },
          { type: 'other', label: 'Other Device' },
        ].map(({ type, label }) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: getNodeColor(type as GraphNode['type'], 0.9),
              border: `2px solid ${getNodeColor(type as GraphNode['type'], 1)}`,
            }} />
            <span style={{ color: '#9ca3af' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(83, 189, 228, 0.2)',
          minHeight: 600,
        }}
      >
        {dimensions && (
          <canvas
            ref={canvasRef}
            width={dimensions.width}
            height={dimensions.height}
            onMouseMove={handleMouseMove}
            onClick={handleClick}
            style={{ display: 'block' }}
          />
        )}

        {/* Selected Node Panel */}
        {selectedNode && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '280px',
            background: 'rgba(13, 17, 23, 0.95)',
            border: `2px solid ${getNodeColor(selectedNode.type, 0.6)}`,
            borderRadius: '12px',
            padding: '1rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ margin: 0, color: '#fff', fontSize: '1rem' }}>
                  {getNodeIcon(selectedNode.type)} {selectedNode.label}
                </h3>
                <p style={{ margin: '0.25rem 0 0 0', color: getNodeColor(selectedNode.type, 1), fontSize: '0.85rem' }}>
                  {selectedNode.sublabel}
                </p>
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginTop: '1rem', fontSize: '0.85rem' }}>
              {selectedNode.ip && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6c757d' }}>IP Address</span>
                  <span style={{ color: '#fff', fontFamily: 'monospace' }}>{selectedNode.ip}</span>
                </div>
              )}
              {selectedNode.mac && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6c757d' }}>MAC</span>
                  <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '0.75rem' }}>{selectedNode.mac}</span>
                </div>
              )}
              {selectedNode.online !== undefined && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#6c757d' }}>Status</span>
                  <span style={{ color: selectedNode.online ? '#22c55e' : '#ef4444' }}>
                    {selectedNode.online ? 'Online' : 'Offline'}
                  </span>
                </div>
              )}
              {selectedNode.networkHost?.open_ports && (
                <div style={{ marginTop: '0.75rem' }}>
                  <span style={{ color: '#6c757d', display: 'block', marginBottom: '0.25rem' }}>Open Ports</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {[...selectedNode.networkHost.open_ports.jetson_typical, ...selectedNode.networkHost.open_ports.siemens_typical].map((port) => (
                      <span key={port} style={{
                        padding: '0.15rem 0.4rem',
                        background: 'rgba(83, 189, 228, 0.2)',
                        borderRadius: '4px',
                        color: '#53bde4',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                      }}>
                        {port}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selectedNode.networkHost?.classification_reasons && selectedNode.networkHost.classification_reasons.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <span style={{ color: '#6c757d', display: 'block', marginBottom: '0.25rem' }}>Classification</span>
                  <ul style={{ margin: 0, padding: '0 0 0 1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                    {selectedNode.networkHost.classification_reasons.slice(0, 3).map((reason, i) => (
                      <li key={i}>{reason}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NetworkGraph;
